import {
  Avatar,
  BellIcon,
  DensityMediumIcon,
  HelpIcon,
  Popover,
  Tooltip,
  useStatefulRef,
} from "@forge/common";
import { btnResponsive } from "../../academy/common/utils/helper";
import useResponsiveSize from "../../academy/common/utils/useWindowSize";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./header.scss";
import { AppState } from "../../../../common/store/combinedReducer";
import { useAuth } from "../../../../common/hooks/useAuth";
import { useMemo, useState, useEffect } from "react";
import feedback from "../../../../common/assets/feedback.png";
import Button from "../../academy/common/components/button/button";
import config from "../../../../common/authentication/config";
import AboutUsPopup from "../../../../common/components/AboutUsPopup/AboutUsPopup";
import {
  FeedbackModal,
  FeedbackModalConfirmation,
} from "../../../../common/utility/Feedback/FeedbackModal";
import {
  getFeedbackData,
  setFeedbackStatus,
  setLoading,
} from "../../../store/action";

interface HeaderProps {
  onMenuToggle: () => void;
  sidebarOpen: boolean;
  profileOnly?: boolean;
}
const HeaderLanding: React.FC<HeaderProps> = ({
  onMenuToggle,
  sidebarOpen,
  profileOnly = false,
}) => {
  const anchor = useStatefulRef<HTMLDivElement>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    hasAnyRole,
    hasAcademyAccess,
    isPermissionsLoading,
    userInfo,
    hasPermission,
    isAcademyInstructorOnly,
  } = useAuth();

  const [feedbackPopOpen, setFeedbackPopOpen] = useState(false);
  const [feedbackSubjectInput, setFeedbackSubject] = useState("");
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackConfirmation, setFeedbackConfirmation] = useState(false);
  const [aboutUsPopOpen, setAboutUsPopOpen] = useState(false);

  // Memoized value to determine if Course Admin button should be visible
  // Only show the button when permissions are loaded AND user has academy access
  const shouldShowCourseAdminButton = useMemo(() => {
    const isLoading = isPermissionsLoading();
    const hasAccess = hasAcademyAccess();

    // Only show button when NOT loading AND has access
    return !isLoading && hasAccess;
  }, [isPermissionsLoading, hasAcademyAccess]);

  const userDetail: any = useSelector((state: AppState) => {
    return state.reducer.userInfo;
  });
  const siteId: any = useSelector((state: AppState) => {
    return state.reducer.siteId;
  });
  const selectedPlant: any = useSelector((state: AppState) => {
    return state.reducer.selectedPlant;
  });

  const feedBackStatus = useSelector((state: AppState) => {
    return state.reducer.feedBackStatus;
  });
  const selectedTenant = useSelector((state: AppState) => {
    return state.reducer.selectedTenant;
  });

  const goToAssistance = () => {
    if (selectedTenant === config.ACADEMY_TENANT_ID) {
      window
        .open(
          "/helpservice/Content/Academy/Content/Resources/Station_Help_Landing_Pages/Home_for_HA_UG.htm",
          "",
          "noopener",
        )
        ?.focus();
    } else {
      window
        .open(
          "/helpservice/Content/Resources/Station_Help_Landing_Pages/Home.htm",
          "",
          "noopener",
        )
        ?.focus();
    }
  };

  const goToAcademy = () => {
    window
      .open(
        "https://process.honeywell.com/us/en/services/training",
        "",
        "noopener",
      )
      ?.focus();
  };

  const submitFeedback = () => {
    dispatch(
      getFeedbackData({
        feedbackData: {
          subject: feedbackSubjectInput,
          description: feedbackText,
        },
        siteId: localStorage.getItem("siteId"),
        emailId: userDetail.email,
        accessToken: localStorage.getItem("access_token"),
      }),
    );
    setFeedbackPopOpen(false);
    setFeedbackSubject("");
    setFeedbackText("");
    dispatch(setLoading(true));
  };

  const handleClose = () => {
    setFeedbackConfirmation(false);
    dispatch(setFeedbackStatus({ status: 0 }));
  };

  const aboutUsPopup = () => {
    return (
      <AboutUsPopup
        heading="About Honeywell Digital Prime Twin"
        version={`Version ${config.VERSION}`}
        patent="Pat.: honpat.com"
        onClose={() => setAboutUsPopOpen(false)}
      />
    );
  };

  useEffect(() => {
    if (feedBackStatus !== 0 && feedBackStatus !== undefined) {
      setFeedbackConfirmation(true);
      dispatch(setLoading(false));
    } else {
      dispatch(setLoading(false));
    }
  }, [feedBackStatus]);

  const goToHAC = () => {
    // If the button is visible, we know permissions are loaded and user has access
    // So we can safely navigate
    if (shouldShowCourseAdminButton) {
      // Store current siteId, tenantId and index so academy can navigate back correctly
      localStorage.setItem("selectedPlant", selectedPlant || "");
      localStorage.setItem("selectedTenant", selectedTenant || "");
      localStorage.setItem(
        "selectedIndex",
        localStorage.getItem("index") || "0",
      );

      // Check permission directly and route to the correct page
      // This avoids new tab permission loading issues by making the decision here
      const hasCoursePermission = !isAcademyInstructorOnly() && hasPermission("dp.twin.course.library.view", true);

      if (hasCoursePermission) {
        window.open("/academy/viewCourseTemplates", "", "noopener")?.focus();
      } else {
        window.open("/academy/iso-file-management", "", "noopener")?.focus();
      }
    } else {
      // This should rarely happen since button shouldn't be clickable if user doesn't have access
      // But as a fallback, redirect to dashboard
      navigate("/dashboard/?siteId=" + siteId + "&tenantId=" + selectedTenant); // changes made here to pass refer for dashboard
    }
  };
  return (
    <div
      className={`flex gap-2 dashboard-header ${sidebarOpen ? "sidebar-open" : ""}`}
    >
      <DensityMediumIcon
        id="acc-header-id"
        data-testid="acc-header-id"
        className={`cursor-pointer hamburgerIcon ${btnResponsive(
          "panel-icons",
        )} ${sidebarOpen ? "sidebar-active" : ""}`}
        onClick={onMenuToggle}
      />
      <div className="flex items-center gap-3 landing-header-right-items header-icons-container">
        {!profileOnly && shouldShowCourseAdminButton && (
          <Tooltip content="Course Admin" position="bottom">
            <div
              className="rounded-full w-[40px] h-[40px] bg-[#1792E5] hover:bg-[#1792E5] flex items-center justify-center cursor-pointer"
              data-testid="header-HAC"
              onClick={() => goToHAC()}
            >
              <img
                src="/course-admin-button-icon.svg"
                alt="Course Admin"
                className="w-[20px] h-[20px]"
              />
            </div>
          </Tooltip>
        )}
        {!profileOnly && (
          <Tooltip content="Feedback" position="bottom">
            <div
              className="rounded-full w-[40px] h-[40px] bg-[#262626] hover:bg-[#3a3a3a] flex items-center justify-center cursor-pointer"
              data-testid="header-Feedback"
              onClick={() => setFeedbackPopOpen(true)}
            >
              <img
                src={feedback}
                alt="Feedback"
                className="w-[20px] h-[20px]"
              />
            </div>
          </Tooltip>
        )}
        {!profileOnly && (
          <Tooltip content="E-Learning" position="bottom">
            <div
              className="rounded-full w-[40px] h-[40px] bg-[#262626] hover:bg-[#3a3a3a] flex items-center justify-center cursor-pointer"
              data-testid="header-graduation"
              onClick={() => goToAcademy()}
            >
              <BellIcon className="text-white text-[20px]" />
            </div>
          </Tooltip>
        )}
        {!profileOnly && (
          <Tooltip
            content="Digital Prime Twin User Assistance"
            position="bottom"
          >
            <button
              type="button"
              className="rounded-full w-[40px] h-[40px] bg-[#262626] hover:bg-[#3a3a3a] flex items-center justify-center cursor-pointer"
              aria-label="Digital Prime Twin User Assistance"
              data-testid="help-icon-id"
              onClick={goToAssistance}
            >
              <HelpIcon className="text-white text-[25px]" />
            </button>
          </Tooltip>
        )}
        <div ref={anchor} className="w-min" style={{ borderRadius: "100%" }}>
          <Avatar
            interactive
            size={useResponsiveSize() === "regular" ? "md" : "lg"}
            aria-label="profile"
            text={userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : ""}
          />
          <Popover
            id="popover-t"
            position="top"
            anchor={anchor}
            attachEvent={true}
            anchorActiveClassName="active"
          >
            <div
              data-testid="profile-popover"
              className="h-auto dark:border-gray-410 dark:bg-container-dark popover-options-Landing"
            >
              <p data-testid="user-name" className="userData-Landing">
                {userInfo?.name}
              </p>
              <p
                id="about-us-link-id"
                data-testid="about-us-link"
                className="cursor-pointer text-white"
                onClick={() => setAboutUsPopOpen(true)}
              >
                About Us
              </p>
              <p
                id="logout-link"
                data-testid="logout-link"
                className="cursor-pointer text-white"
                onClick={() => {
                  localStorage.setItem("logged_out_from", "dashboard header");
                  navigate("/logout");
                }}
              >
                Log Out
              </p>
            </div>
          </Popover>
        </div>
      </div>
      {aboutUsPopOpen && (
        <div className="aboutus-overlay">{aboutUsPopup()}</div>
      )}
      {feedbackPopOpen && (
        <FeedbackModal
          setFeedbackPopOpen={setFeedbackPopOpen}
          feedbackPopOpen={feedbackPopOpen}
          feedbackSubjectInput={feedbackSubjectInput}
          setFeedbackSubject={setFeedbackSubject}
          setFeedbackText={setFeedbackText}
          feedbackText={feedbackText}
          submitFeedback={submitFeedback}
        />
      )}
      {feedbackConfirmation && (
        <FeedbackModalConfirmation
          setFeedbackConfirmation={setFeedbackConfirmation}
          feedbackConfirmation={feedbackConfirmation}
          feedBackStatus={feedBackStatus}
          handleClose={handleClose}
        />
      )}
    </div>
  );
};
export default HeaderLanding;
